import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaTimes } from "react-icons/fa";
import ActionDropdown from "../components/ActionDropdown";
import ClientRealLayout from "./Layout";
import ClientPageHeader from "./ClientPageHeader";
import usePageMeta from "../hooks/usePageMeta";
import useAuth from "../hooks/useAuth";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  CLIENT_FILES_DEFAULT_FILTERS,
  CLIENT_FILES_SORT_OPTIONS,
  buildClientFilesFilterChips,
  ClientFilesFilterFields,
} from "../components/filters";

function normalizeFileType(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function compareValues(a, b, dir = "asc") {
  if (a == null) return dir === "asc" ? -1 : 1;
  if (b == null) return dir === "asc" ? 1 : -1;
  const aNum = parseFloat(a);
  const bNum = parseFloat(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return dir === "asc" ? aNum - bNum : bNum - aNum;
  }
  const res = String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
  return dir === "asc" ? res : -res;
}

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "svg",
]);

function getFileExtension(name) {
  const match = String(name || "").match(/\.([^.]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

function isImageFile(file) {
  const ext =
    getFileExtension(file?.name) ||
    String(file?.extension || "").toLowerCase().replace(/^\./, "");
  if (IMAGE_EXTENSIONS.has(ext)) return true;
  const mime = String(file?.mime_type || "").toLowerCase();
  return mime.startsWith("image/");
}

function isPdfFile(file) {
  const ext =
    getFileExtension(file?.name) ||
    String(file?.extension || "").toLowerCase().replace(/^\./, "");
  const mime = String(file?.mime_type || file?.content_type || "").toLowerCase();
  return ext === "pdf" || mime === "application/pdf";
}

async function readDownloadErrorMessage(res) {
  const text = await res.text().catch(() => "");
  try {
    const j = JSON.parse(text);
    return j?.message || j?.error || text || "Failed to download file.";
  } catch {
    return (text && String(text).trim().slice(0, 400)) || "Failed to download file.";
  }
}

const ClientFiles = () => {
  usePageMeta("Files");
  const auth = useAuth();
  const filterPanel = useFilterPanel();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [fileTypeFilters, setFileTypeFilters] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.fileTypeFilters,
  );
  const [statusFilters, setStatusFilters] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.statusFilters,
  );
  const [sortBy, setSortBy] = useState(CLIENT_FILES_DEFAULT_FILTERS.sortBy);
  const [sortDirection, setSortDirection] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.sortDirection,
  );
  const [pageSize, setPageSize] = useState(10);

  const getClientId = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (token && token !== "null" && token !== "undefined") {
        const payload = token.split(".")[1];
        if (payload) {
          const decoded = JSON.parse(
            atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
          );
          if (decoded?.client_id) return String(decoded.client_id);
        }
      }
    } catch (e) {
      console.warn("Could not decode token for client_id", e);
    }
    return localStorage.getItem("clientId") || "";
  }, []);

  const fetchFiles = useCallback(async () => {
    const clientId = getClientId();
    if (!clientId) return;

    setLoading(true);
    try {
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/clients/${clientId}/files`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const json = await res.json();
      setFiles(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      console.error("Failed to fetch files", e);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.token, getClientId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    return () => {
      if (imagePreview?.revoke && imagePreview?.url) {
        URL.revokeObjectURL(imagePreview.url);
      }
    };
  }, [imagePreview]);

  const resolveFileDownloadUrl = async (file) => {
    const clientId = getClientId();
    if (!clientId) throw new Error("Client ID not found");
    const fid = file?.id ?? file?.file_id;
    if (!fid) throw new Error("Missing file id.");

    const token = auth?.token || localStorage.getItem("token");
    const res = await fetch(
      `${BASE_URL}/api/clients/${clientId}/files/${fid}/download`,
      {
        redirect: "manual",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );

    if (res.status === 404) {
      throw new Error(await readDownloadErrorMessage(res));
    }
    if (res.status === 302) {
      const redirectUrl = res.headers.get("Location");
      if (redirectUrl) {
        return {
          url: redirectUrl,
          revoke: false,
          mimeType: res.headers.get("content-type") || "",
        };
      }
    }
    if (res.ok) {
      const blob = await res.blob();
      return {
        url: URL.createObjectURL(blob),
        revoke: true,
        mimeType: blob.type || "",
      };
    }
    throw new Error(await readDownloadErrorMessage(res));
  };

  const closeImagePreview = useCallback(() => {
    setImagePreview((prev) => {
      if (prev?.revoke && prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const filteredAndSortedFiles = useMemo(() => {
    let filtered = (files || []).filter(
      (file) => (file.state || "").toLowerCase() !== "deleted",
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (file) =>
          (file.name || "").toLowerCase().includes(query) ||
          (file.note || "").toLowerCase().includes(query) ||
          (file.type || "").toLowerCase().includes(query) ||
          (file.client_name || "").toLowerCase().includes(query),
      );
    }

    if (fileTypeFilters.length > 0) {
      const allowed = new Set(fileTypeFilters.map((t) => normalizeFileType(t)));
      filtered = filtered.filter((file) =>
        allowed.has(normalizeFileType(file.type)),
      );
    }

    if (statusFilters.length > 0) {
      const allowed = new Set(statusFilters.map((s) => String(s).toLowerCase()));
      filtered = filtered.filter((file) =>
        allowed.has((file.state || "").toLowerCase()),
      );
    }

    filtered.sort((a, b) => {
      const sortField = (file) => {
        if (sortBy === "name") return file.name || "";
        if (sortBy === "status") return file.state || "";
        if (sortBy === "type") return file.type || "";
        if (sortBy === "expiry") return file.expiry_date || "";
        return file.name || "";
      };
      return compareValues(sortField(a), sortField(b), sortDirection);
    });

    return filtered;
  }, [
    files,
    searchQuery,
    fileTypeFilters,
    statusFilters,
    sortBy,
    sortDirection,
  ]);

  const filterChips = useMemo(
    () =>
      buildClientFilesFilterChips({
        fileTypeFilters,
        statusFilters,
        showDeleted: false,
      }),
    [fileTypeFilters, statusFilters],
  );

  const visibleFiles = filteredAndSortedFiles.slice(0, pageSize);
  const resultsStart = filteredAndSortedFiles.length ? 1 : 0;
  const resultsEnd = Math.min(filteredAndSortedFiles.length, pageSize);

  const clearFilters = () => {
    setFileTypeFilters(CLIENT_FILES_DEFAULT_FILTERS.fileTypeFilters);
    setStatusFilters(CLIENT_FILES_DEFAULT_FILTERS.statusFilters);
    setSortBy(CLIENT_FILES_DEFAULT_FILTERS.sortBy);
    setSortDirection(CLIENT_FILES_DEFAULT_FILTERS.sortDirection);
  };

  const removeFilterChip = (chip) => {
    const key = chip?.key;
    if (key === "fileTypeFilters") setFileTypeFilters([]);
    if (key === "statusFilters") setStatusFilters([]);
  };

  const handleFileColumnSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDirection("asc");
  };

  const renderSortArrow = (key) => {
    if (sortBy !== key) return <span className="ml-1 opacity-70">⇅</span>;
    return (
      <span className="ml-1">{sortDirection === "asc" ? "▲" : "▼"}</span>
    );
  };

  const handleViewFile = async (file) => {
    try {
      const { url, revoke } = await resolveFileDownloadUrl(file);
      if (revoke) {
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name || "download";
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error("Failed to download file", e);
      showError(e?.message || "Failed to download file. Please try again.");
    }
  };

  const handleImagePreview = async (file) => {
    setImagePreview({ file, url: null, loading: true, revoke: false });
    try {
      const { url, revoke, mimeType } = await resolveFileDownloadUrl(file);
      setImagePreview({
        file,
        url,
        loading: false,
        revoke,
        type: isImageFile(file) || mimeType.startsWith("image/")
          ? "image"
          : isPdfFile(file) || mimeType === "application/pdf"
            ? "pdf"
            : "unsupported",
      });
    } catch (e) {
      console.error("Failed to preview file", e);
      setImagePreview(null);
      showError(e?.message || "Failed to preview file. Please try again.");
    }
  };

  const handleFileReupload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedFile) return;

    const clientId = getClientId();
    if (!clientId) {
      showError("Client ID not found");
      return;
    }

    setUploadProgress(true);
    try {
      const token = auth?.token || localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const rid = selectedFile.id ?? selectedFile.file_id;
      if (!rid) {
        showError("Missing file id.");
        return;
      }
      const res = await fetch(
        `${BASE_URL}/api/clients/${clientId}/files/${rid}`,
        {
          method: "PUT",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        },
      );

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = {};
      }
      if (json.success || res.ok) {
        showSuccess("File reuploaded successfully");
        setShowReuploadModal(false);
        setSelectedFile(null);
        fetchFiles();
      } else {
        showError(
          json.message ||
            (typeof raw === "string" && raw.trim().slice(0, 300)) ||
            "Failed to reupload file",
        );
      }
    } catch (e) {
      console.error("Reupload error", e);
      showError("Failed to reupload file. Please try again.");
    } finally {
      setUploadProgress(false);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <ClientRealLayout>
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-background">
        <ClientPageHeader title="Files" />

        <div
          className={`flex-1 min-h-0 overflow-hidden flex flex-col px-6 pt-3 pb-2 ${filterPanel.contentOffsetClass}`}
        >
          <div className="mt-3 flex-1 min-h-0 p-4 border-2 border-gray-200 rounded-xl flex flex-col min-w-0 bg-gray-50 overflow-hidden">
            <div className="flex-shrink-0">
              <AtlasListToolbar
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search files"
                onFilterClick={filterPanel.toggle}
                filterOpen={filterPanel.isOpen}
                onRefresh={fetchFiles}
                refreshSpinning={loading}
                resultsMeta={{
                  startRow: resultsStart,
                  endRow: resultsEnd,
                  totalCount: filteredAndSortedFiles.length,
                  pageSize,
                  pageSizeOptions: [10, 25, 50, 100],
                  onPageSizeChange: setPageSize,
                  sortBy,
                  sortOrder: sortDirection,
                  sortOptions: CLIENT_FILES_SORT_OPTIONS,
                  onSortByChange: setSortBy,
                  onSortOrderToggle: () =>
                    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
                }}
                chipBar={
                  <AtlasFilterChipBar
                    chips={filterChips}
                    onChipClick={(chip) => {
                      if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                      else filterPanel.open();
                    }}
                    onRemove={removeFilterChip}
                    onClearAll={clearFilters}
                  />
                }
              />
            </div>

            <div className="flex-1 min-h-0 min-w-0 mt-3">
              {loading ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Loading files…
                </div>
              ) : filteredAndSortedFiles.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No files found for the selected filters.
                </div>
              ) : (
                <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto overflow-y-auto h-full">
                  <table className="client-files-table w-full min-w-[980px] border-collapse !table-auto">
                    <colgroup>
                      <col style={{ width: "36%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "8%" }} />
                    </colgroup>
                    <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                      <tr>
                        <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                          <button
                            type="button"
                            onClick={() => handleFileColumnSort("name")}
                            className="inline-flex items-center hover:underline text-left"
                          >
                            Name
                            {renderSortArrow("name")}
                          </button>
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                          Client
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                          Note
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleFileColumnSort("status")}
                            className="inline-flex items-center hover:underline"
                          >
                            State
                            {renderSortArrow("status")}
                          </button>
                        </th>
                        <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleFileColumnSort("type")}
                            className="inline-flex items-center hover:underline"
                          >
                            Type
                            {renderSortArrow("type")}
                          </button>
                        </th>
                        <th className="py-2 px-3 text-right text-xs font-medium text-white align-top whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleFiles.map((file) => (
                        <tr
                          key={file.id || file.file_id}
                          className="border-t border-border hover:bg-table-row-hover transition-colors align-top"
                        >
                          <td className="client-files-name-cell py-2 px-3 align-top">
                            {isImageFile(file) || isPdfFile(file) ? (
                              <button
                                type="button"
                                onClick={() => handleImagePreview(file)}
                                className="text-left text-sm font-medium text-red-600 hover:underline cursor-pointer break-words [overflow-wrap:anywhere]"
                              >
                                {file.name || "Untitled"}
                              </button>
                            ) : (
                              <span className="text-sm font-medium text-foreground break-words [overflow-wrap:anywhere]">
                                {file.name || "Untitled"}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm text-foreground align-top whitespace-nowrap">
                            {file.client_name || "Client"}
                          </td>
                          <td className="py-2 px-3 text-sm text-muted-foreground break-words [overflow-wrap:anywhere] align-top">
                            {file.note || ""}
                          </td>
                          <td className="py-2 px-3 text-sm text-foreground capitalize align-top whitespace-nowrap">
                            {file.state || ""}
                          </td>
                          <td className="py-2 px-3 text-sm text-foreground align-top break-words [overflow-wrap:anywhere]">
                            {file.type || ""}
                          </td>
                          <td className="py-2 px-3 text-right align-top whitespace-nowrap">
                            <div className="flex justify-end">
                              <ActionDropdown
                                actions={[
                                  {
                                    label: "Preview",
                                    onClick: () => handleImagePreview(file),
                                  },
                                  {
                                    label: "Download",
                                    onClick: () => handleViewFile(file),
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AtlasFilterPanel
        isOpen={filterPanel.isOpen}
        onClose={filterPanel.close}
        onClear={clearFilters}
        activeCount={filterChips.length}
      >
        <ClientFilesFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          fileTypeFilters={fileTypeFilters}
          setFileTypeFilters={setFileTypeFilters}
          statusFilters={statusFilters}
          setStatusFilters={setStatusFilters}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
        />
      </AtlasFilterPanel>

      {imagePreview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
          onClick={closeImagePreview}
          role="presentation"
        >
          <div
            className="relative flex max-h-[95vh] max-w-[95vw] flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="File preview"
          >
            <button
              type="button"
              onClick={closeImagePreview}
              className="absolute -top-2 -right-2 z-10 rounded-full bg-white p-2 text-gray-700 shadow-md hover:bg-gray-100"
              aria-label="Close preview"
            >
              <FaTimes className="h-4 w-4" />
            </button>
            {imagePreview.loading ? (
              <div className="rounded-lg bg-white px-8 py-10 text-sm text-gray-600">
                Loading preview…
              </div>
            ) : imagePreview.type === "pdf" ? (
              <iframe
                src={imagePreview.url}
                title={imagePreview.file?.name || "PDF preview"}
                className="h-[90vh] w-[90vw] max-w-5xl rounded-lg bg-white shadow-2xl"
              />
            ) : imagePreview.type === "unsupported" ? (
              <div className="rounded-lg bg-white px-8 py-10 text-sm text-gray-600 shadow-2xl">
                This file type cannot be previewed in the application.
              </div>
            ) : (
              <img
                src={imagePreview.url}
                alt={imagePreview.file?.name || "Image preview"}
                className="max-h-[90vh] max-w-[90vw] rounded-lg bg-white object-contain shadow-2xl"
              />
            )}
          </div>
        </div>
      ) : null}

      {showReuploadModal && selectedFile ? (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                File Reupload Required
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowReuploadModal(false);
                  setSelectedFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                This file was saved before we implemented S3 storage method.
                Please reupload the file.
              </p>
              <p className="text-sm font-medium text-gray-900">
                File: {selectedFile.name || "Untitled"}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File to Reupload
              </label>
              <input
                type="file"
                onChange={handleFileReupload}
                disabled={uploadProgress}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {uploadProgress ? (
              <div className="text-sm text-gray-600 text-center">
                Uploading file...
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </ClientRealLayout>
  );
};

export default ClientFiles;
